import Flatten from "@flatten-js/core";
import { throttle } from "es-toolkit";
import { orderBy } from "es-toolkit/compat";
import Paper from "paper/dist/paper-core";
import { mix } from "polished";
import { useEffect, useMemo, useState } from "react";

import { getConnectorCurveDescriptor } from "./curvableConnector.tsx";
import { defaultCurveConfig } from "./curve.ts";
import { getGeometricConnector } from "./geometricConnector.ts";
import { getGeometricScene } from "./geometricScene.ts";
import type { BoxedConnector, ConnectorEdges, CurveConfig, GeometricConnector, GeometricScene } from "./types.ts";

const { point } = Flatten;

type Candidate = {
  connector: GeometricConnector;
  penalties: Record<string, boolean | number>;
  path: paper.Path;
};

const highlightColor = new Paper.Color("green");

/*
  This function is only ever called in debug mode (when DEBUG_PAPER is set to true),
  so it is safe to call `alert`
*/
const paintSortedCandidates = (sortedCandidates: Candidate[]) => {
  sortedCandidates.forEach((c, i) => {
    const color = mix(i / 15, "#f00", "#00f");
    const strokeColor = new Paper.Color(color);
    c.path.strokeWidth = 2;
    c.path.strokeColor = strokeColor;
    c.path.onClick = () => {
      alert(`rank: ${i + 1}\n${JSON.stringify(c.penalties, null, 4)}`);
    };
    c.path.onMouseEnter = () => {
      c.path.bringToFront();
      c.path.strokeColor = highlightColor;
    };
    c.path.onMouseLeave = () => {
      c.path.strokeColor = strokeColor;
    };
  });
};

/*
  Algorithm to define the optimal anchor points to use to draw a connector between two scenes.

  A scene comprises anchor points on each of its 4 edges: TOP, BOTTOM, LEFT, RIGHT.
  So there are 16 combinations of these points that are potential connectors between two scenes.
  We perform a series of geometric computations against each of these candidates in order to rank them.

  See the notion page for the specification for ranking candidates:
  https://www.notion.so/skilitics/Optimal-anchor-points-3e3239700b2143cfa14000a086efb7e0
*/

export const getOptimalEdges = (
  fromGeometry: GeometricScene,
  toGeometry: GeometricScene,
  options: CurveConfig,
  debugPaper = false,
): ConnectorEdges => {
  /*
    These two rectangles are used to check for intersections between a candidate and the scenes.

    Since Paper.js considers a "touching" point as an intersection, we trim 0.0001px off
    of all sides so that the start and end points of the candidates aren't counted.
  */
  const reduction = 0.0001;

  const fromRectangle = new Paper.Path.Rectangle(
    new Paper.Point(fromGeometry.polygon.box.xmin + reduction, fromGeometry.polygon.box.ymin + reduction),
    new Paper.Point(fromGeometry.polygon.box.xmax - reduction, fromGeometry.polygon.box.ymax - reduction),
  );
  const toRectangle = new Paper.Path.Rectangle(
    new Paper.Point(toGeometry.polygon.box.xmin + reduction, toGeometry.polygon.box.ymin + reduction),
    new Paper.Point(toGeometry.polygon.box.xmax - reduction, toGeometry.polygon.box.ymax - reduction),
  );

  // array of all candidates
  const candidates = fromGeometry.anchors.flatMap((fromAnchor) =>
    toGeometry.anchors.map((toAnchor) => ({ from: fromAnchor, to: toAnchor })),
  );

  const augmentedCandidates = candidates.map(({ from, to }) => {
    const connector = getGeometricConnector(from, to);
    const { isOppositeAndInversed, isOppositeAndClose } = connector;

    // Checks for when an anchor point overlaps the other scene
    const isOverlappingScene =
      toGeometry.polygon.contains(point(from.point.x, from.point.y)) ||
      fromGeometry.polygon.contains(point(to.point.x, to.point.y));

    const { startOffset, startCurve, controlPointA, controlPointB, endCurve, endOffset } = getConnectorCurveDescriptor({
      connector,
      options,
    });

    // Paperjs representation of a connector's path.
    const path = new Paper.Path({
      segments: [
        [[from.point.x, from.point.y], null, null],
        [[startOffset.x, startOffset.y], null, null],
        [[startCurve.x, startCurve.y], null, [controlPointA.x - startCurve.x, controlPointA.y - startCurve.y]],
        [[endCurve.x, endCurve.y], [controlPointB.x - endCurve.x, controlPointB.y - endCurve.y], null],
        [[endOffset.x, endOffset.y], null, null],
        [[to.point.x, to.point.y], null, null],
      ],
    });

    const isIntersectingScene = path.intersects(fromRectangle) || path.intersects(toRectangle);

    /*
      Paperjs typings don't cover the entire API.
      `path.getIntersections` expects a Path argument, but an empty argument is valid.

      A Pull Request was opened against the Paper.js Github repository to fix this:
      https://github.com/paperjs/paper.js/pull/1872

      TODO: Remove the ts-ignore annotation once the PR is merged
    */
    // @ts-ignore
    const isIntersectingSelf = path.getIntersections().length > 0;

    const length = path.length;
    const isPathTooShort = path.length < 30;

    return {
      connector,
      penalties: {
        isOppositeAndInversed,
        isOverlappingScene,
        isIntersectingScene,
        isIntersectingSelf,
        isOppositeAndClose,
        isPathTooShort,
        length,
      },
      path,
    };
  });

  const sortedCandidates = orderBy(augmentedCandidates, [
    "penalties.isOppositeAndInversed",
    "penalties.isOverlappingScene",
    "penalties.isIntersectingSelf",
    "penalties.isIntersectingScene",
    "penalties.isOppositeAndClose",
    "penalties.isPathTooShort",
    "penalties.length",
  ]);

  if (debugPaper) {
    paintSortedCandidates(sortedCandidates);
  }

  return {
    from: sortedCandidates[0].connector.from.edge,
    to: sortedCandidates[0].connector.to.edge,
  };
};

const getOptimalEdgesMap = (
  connectors: BoxedConnector[],
  options: CurveConfig,
  debugPaper = false,
): Record<string, ConnectorEdges> =>
  Object.fromEntries(
    connectors.map((connector) => [
      connector.id,
      getOptimalEdges(
        getGeometricScene(connector.from.bounds),
        getGeometricScene(connector.to.bounds),
        options,
        debugPaper,
      ),
    ]),
  );

export function useOptimalEdges(
  connectors: BoxedConnector[],
  movingSceneId: number | string | undefined,
  beforeComputationCallback: () => void,
  options: CurveConfig = defaultCurveConfig,
  debugPaper = false,
) {
  const [optimalEdges, setOptimalEdges] = useState(() => getOptimalEdgesMap(connectors, options, debugPaper));

  const throttledFn = useMemo(
    () =>
      throttle(
        (throttledConnectors: BoxedConnector[], throttledMovingSceneId?: number | string) => {
          beforeComputationCallback();
          setOptimalEdges((currentOptimalEdges) => {
            if (!throttledMovingSceneId) return getOptimalEdgesMap(throttledConnectors, options, debugPaper);
            else {
              const movingConnectors = throttledConnectors.filter(
                (c) => c.from.id === throttledMovingSceneId || c.to.id === throttledMovingSceneId,
              );
              return {
                ...currentOptimalEdges,
                ...getOptimalEdgesMap(movingConnectors, options, debugPaper),
              };
            }
          });
        },
        1000,
        { edges: ["leading"] },
      ),
    [beforeComputationCallback, debugPaper, options],
  );

  useEffect(() => {
    throttledFn(connectors, movingSceneId);
    return () => {
      throttledFn.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectors, movingSceneId]);

  return optimalEdges;
}
